import { Google } from './Google'
import { Button, Alert, space } from '@ticketswap/solar'
import { useEffect, useState, useContext } from 'react'
import styled from '@emotion/styled'
import { GoogleContext } from '~/components/GoogleContext'
import { useMutation } from '@apollo/client'
import { AUTHENTICATE_CLIENT_FOR_TOKEN } from '~/graphql/mutations/authenticateClientForToken'
import { AUTHENTICATE_USER_WITH_GOOGLE } from '~/graphql/mutations/authenticateUserWithGoogle'
import {
  AUTHENTICATION_COMPLETED,
  AUTHENTICATION_STARTED,
  MISSING_NAME_FROM_PROVIDER,
} from '~/lib/tracking-events'
import { track, setTrackingIdentity } from '~/utils/analytics'
import { useTranslation } from '~/components/TranslationContext'
import getConfig from 'next/config'
import { setCookie } from 'cookies-next'
import { DAY_IN_SECONDS, MONTH_IN_SECONDS } from '~/lib/timeInSeconds'

const { publicRuntimeConfig } = getConfig()

const dev = process.env.NODE_ENV !== 'production'

const StyledAlert = styled(Alert)`
  margin-block-end: ${space[8]};
`

const NAME_MISSING_ERROR = 'The first and last name are required for new users.'

const GoogleAuthButton = ({ children, onLogin, onSignup }) => {
  const { t } = useTranslation()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { initialize, isInitialized, login, isAvailable, logout } = useContext(GoogleContext)
  const [generate, { client }] = useMutation(AUTHENTICATE_CLIENT_FOR_TOKEN)
  const [authenticate] = useMutation(AUTHENTICATE_USER_WITH_GOOGLE)

  useEffect(() => {
    if (!isInitialized && !dev) {
      initialize()
    }
  }, [initialize, isInitialized])

  const handleCompleteLogin = async res => {
    const {
      data: { authenticateClientForToken },
    } = await generate({
      variables: {
        input: {
          clientId: publicRuntimeConfig.NEXT_PUBLIC_CLIENT_ID,
        },
      },
    })

    const { access_token: googleAccessToken, profile } = res
    const { first_name: firstName, last_name: lastName } = profile
    const { token } = authenticateClientForToken

    if (!Boolean(firstName) || !Boolean(lastName)) {
      track(MISSING_NAME_FROM_PROVIDER, {
        method: 'google',
        firstName: Boolean(firstName),
        lastName: Boolean(lastName),
      })
    }

    let googleAuth

    try {
      const { data } = await authenticate({
        variables: {
          input: {
            token,
            googleAccessToken,
            firstName,
            lastName,
          },
        },
      })

      googleAuth = data.googleAuth
    } catch (error) {
      console.error(error)

      track(AUTHENTICATION_COMPLETED, {
        method: 'google',
        error: error.message,
      })

      setLoading(false)
      await logout()

      if (error.message === NAME_MISSING_ERROR) {
        return setError(t('common:auth.login.google.errors.missingName'))
      }

      return setError(t('common:auth.login.google.errors.generic'))
    }

    const { tokenString } = googleAuth.apiAccessToken

    setCookie(process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME, tokenString, {
      maxAge: MONTH_IN_SECONDS,
      secure: true,
      sameSite: 'lax',
    })

    await client.resetStore()

    await setTrackingIdentity()

    if (googleAuth.isNew) {
      return onSignup({
        firstName,
        lastName,
      })
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'google',
      isNew: false,
    })

    onLogin()
  }

  const handleGoogleLogin = async () => {
    track(AUTHENTICATION_STARTED, {
      method: 'google',
    })

    if (dev) {
      // Get your dev domain e.g. nl.glenn
      const state = window.location.host.split('.').splice(0, 2).join('.')

      // Save pathname so we can redirect later
      setCookie('redirectTo', window.location.pathname, {
        maxAge: DAY_IN_SECONDS,
        secure: true,
        sameSite: 'lax',
      })

      const url = new URL('https://accounts.google.com/o/oauth2/v2/auth')
      url.searchParams.append('state', state)
      url.searchParams.append('scope', 'profile email openid')
      url.searchParams.append('response_type', 'code')
      url.searchParams.append(
        'redirect_uri',
        publicRuntimeConfig.NEXT_PUBLIC_GOOGLE_LOGIN_REDIRECT_URI
      )
      url.searchParams.append(
        'client_id',
        publicRuntimeConfig.NEXT_PUBLIC_AUTHENTICATION_GOOGLE_CLIENT_ID
      )

      return (window.location.href = url.href)
    }

    setLoading(true)
    setError(null)

    try {
      const res = await login()
      handleCompleteLogin(res)
    } catch (errorResponse) {
      setLoading(false)

      if (errorResponse?.error === 'popup_closed_by_user') return
      if (errorResponse?.error === 'access_denied') {
        return setError(t('common:auth.login.google.errors.denied_permissions'))
      }

      return setError(t('common:auth.login.google.errors.not_authenticated'))
    }
  }

  if (!isAvailable) return null

  return (
    <>
      {error && (
        <StyledAlert
          variant="error"
          {...(error === NAME_MISSING_ERROR && {
            action: {
              onClick: () =>
                window.open('https://support.google.com/accounts/answer/27442', '_blank'),
              label: t('common:generic.moreInfo'),
            },
          })}
        >
          {error}
        </StyledAlert>
      )}
      <Button
        variant="google"
        loading={loading}
        disabled={!isInitialized && !dev}
        leftAdornment={<Google size={20} />}
        onClick={handleGoogleLogin}
      >
        {children}
      </Button>
    </>
  )
}

export default GoogleAuthButton
